import React, { useEffect, useState } from "react";
import {
  Alert,
  alpha,
  Avatar,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Assessment,
  CalendarToday,
  Group,
  QueryStats,
  Restaurant,
  Schedule,
  TrendingUp,
  Warning,
} from "@mui/icons-material";
import { productsEventsTimePattern } from "../../../../api/product";

const ProductTimePatternAnalysis = ({ startDate, endDate }) => {
  const theme = useTheme();
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const weekdayNames = [
    "Segunda",
    "Terça",
    "Quarta",
    "Quinta",
    "Sexta",
    "Sábado",
    "Domingo",
  ];

  const complexityColors = {
    simple: {
      bg: theme.palette.success.light,
      text: theme.palette.success.dark,
    },
    medium: {
      bg: theme.palette.warning.light,
      text: theme.palette.warning.dark,
    },
    complex: { bg: theme.palette.error.light, text: theme.palette.error.dark },
  };

  const complexityLabels = {
    simple: "Simples",
    medium: "Médio",
    complex: "Complexo",
  };

  const getProductColor = (index) => {
    const colors = [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.error.main,
      theme.palette.warning.main,
      theme.palette.info.main,
      theme.palette.success.main,
    ];
    return colors[index % colors.length];
  };

  useEffect(() => {
    const fetchAnalysisData = async () => {
      if (!startDate || !endDate) return;

      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          start_date: startDate,
          end_date: endDate,
        });

        const response = await productsEventsTimePattern(params);
        const data = await response.data;
        setAnalysisData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalysisData();
  }, [startDate, endDate]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const truncateProductTitle = (title, maxLength = 15) => {
    if (!title) return "Produto";
    return title.length > maxLength
      ? `${title.substring(0, maxLength)}...`
      : title;
  };

  const renderSummaryStats = () => {
    if (!analysisData) return null;

    const sharedTimePercentage = Math.round(
      (analysisData.sharedKitchenTimeProducts /
        analysisData.totalProductsAnalyzed) *
        100
    );

    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.1
              )} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ backgroundColor: theme.palette.primary.main }}>
                <Restaurant />
              </Avatar>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={500}
                >
                  Produtos Analisados
                </Typography>
                <Typography variant="h6" fontWeight={700} color="primary.main">
                  {analysisData.totalProductsAnalyzed}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.success.main,
                0.1
              )} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ backgroundColor: theme.palette.success.main }}>
                <Assessment />
              </Avatar>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={500}
                >
                  Pedidos Únicos
                </Typography>
                <Typography variant="h6" fontWeight={700} color="success.main">
                  {analysisData.uniqueOrdersAnalyzed}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.warning.main,
                0.1
              )} 0%, ${alpha(theme.palette.warning.main, 0.05)} 100%)`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ backgroundColor: theme.palette.warning.main }}>
                <Group />
              </Avatar>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={500}
                >
                  Tempo Compartilhado
                </Typography>
                <Typography variant="h6" fontWeight={700} color="warning.main">
                  {analysisData.sharedKitchenTimeProducts}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.info.main,
                0.1
              )} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ backgroundColor: theme.palette.info.main }}>
                <QueryStats />
              </Avatar>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={500}
                >
                  % Tempo Compartilhado
                </Typography>
                <Typography variant="h6" fontWeight={700} color="info.main">
                  {sharedTimePercentage}%
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  const renderComplexityAnalysis = () => {
    if (!analysisData?.orderComplexityAnalysis) return null;

    return (
      <Box>
        <Typography variant="h6" fontWeight={600} mb={3} color="text.primary">
          Análise de Complexidade dos Pedidos
        </Typography>

        <Grid container spacing={3}>
          {Object.entries(analysisData.orderComplexityAnalysis).map(
            ([complexity, data]) => (
              <Grid item xs={12} md={4} key={complexity}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${alpha(
                      complexityColors[complexity].bg,
                      0.1
                    )} 0%, ${alpha(
                      complexityColors[complexity].bg,
                      0.05
                    )} 100%)`,
                    border: `1px solid ${alpha(
                      complexityColors[complexity].bg,
                      0.3
                    )}`,
                  }}
                >
                  <Stack spacing={2}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Chip
                        label={complexityLabels[complexity]}
                        sx={{
                          backgroundColor: complexityColors[complexity].bg,
                          color: complexityColors[complexity].text,
                          fontWeight: 600,
                        }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {data.orderCount} pedidos
                      </Typography>
                    </Stack>

                    <Box>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        mb={1}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Tempo Médio:
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {formatTime(data.averageTime)}
                        </Typography>
                      </Stack>

                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        mb={2}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Variação:
                        </Typography>
                        <Typography variant="body2">
                          {formatTime(data.minTime)} -{" "}
                          {formatTime(data.maxTime)}
                        </Typography>
                      </Stack>

                      <Box
                        sx={{
                          backgroundColor: alpha(theme.palette.grey[300], 0.3),
                          borderRadius: 1,
                          height: 6,
                        }}
                      >
                        <Box
                          sx={{
                            height: 6,
                            borderRadius: 1,
                            backgroundColor: complexityColors[complexity].bg,
                            width: `${Math.min(
                              (data.averageTime / 60) * 100,
                              100
                            )}%`,
                          }}
                        />
                      </Box>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            )
          )}
        </Grid>
      </Box>
    );
  };

  const renderHourlyProductPattern = (hourPattern) => {
    const productPatterns = hourPattern.productPatterns || {};

    if (Object.keys(productPatterns).length === 0) {
      return null;
    }

    const hasData = Object.keys(productPatterns).length > 0;
    const topProducts = Object.entries(productPatterns)
      .sort(([, a], [, b]) => b.averageKitchenTime - a.averageKitchenTime)
      .slice(0, 3);

    return (
      <Paper
        key={`hour-${hourPattern.hour}`}
        elevation={1}
        sx={{
          p: 2,
          borderRadius: 3,
          backgroundColor: hasData
            ? alpha(theme.palette.primary.main, 0.02)
            : alpha(theme.palette.grey[300], 0.05),
          border: `1px solid ${
            hasData
              ? alpha(theme.palette.primary.main, 0.1)
              : alpha(theme.palette.grey[300], 0.2)
          }`,
          minHeight: 200,
          minWidth: 180,
        }}
      >
        <Stack spacing={1.5} alignItems="center">
          <Typography
            variant="subtitle2"
            fontWeight={600}
            color={hasData ? "primary" : "text.secondary"}
          >
            {hourPattern.hour}:00
          </Typography>

          {hasData ? (
            <Stack spacing={1.5} width="100%">
              {topProducts.map(([productId, data], index) => (
                <Box key={productId}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={0.5}
                  >
                    <Tooltip title={data.productTitle} arrow>
                      <Chip
                        label={truncateProductTitle(data.productTitle)}
                        size="small"
                        sx={{
                          fontSize: "0.65rem",
                          height: 18,
                          backgroundColor: alpha(getProductColor(index), 0.1),
                          color: getProductColor(index),
                          fontWeight: 600,
                          maxWidth: "70%",
                        }}
                      />
                    </Tooltip>
                    <Typography variant="caption" fontWeight={600}>
                      {formatTime(data.averageKitchenTime)}
                    </Typography>
                  </Stack>

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    mb={0.5}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Qtd: {data.totalQuantity}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Pedidos: {data.orderCount}
                    </Typography>
                  </Stack>

                  {data.sharedTimeInstances > 0 && (
                    <Stack direction="row" justifyContent="center" mb={1}>
                      <Tooltip
                        title={`${data.sharedTimeInstances} de ${data.orderCount} pedidos foram agrupados`}
                        arrow
                      >
                        <Chip
                          icon={<Group />}
                          label={`${Math.round(
                            (data.sharedTimeInstances / data.orderCount) * 100
                          )}% agrupado`}
                          size="small"
                          sx={{
                            fontSize: "0.6rem",
                            height: 16,
                            backgroundColor: alpha(
                              theme.palette.warning.main,
                              0.1
                            ),
                            color: theme.palette.warning.main,
                            "& .MuiChip-icon": { fontSize: "0.7rem" },
                          }}
                        />
                      </Tooltip>
                    </Stack>
                  )}

                  <Box
                    sx={{
                      height: 4,
                      backgroundColor: alpha(getProductColor(index), 0.1),
                      borderRadius: 1,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width: `${Math.min(
                          (data.averageKitchenTime / 60) * 100,
                          100
                        )}%`,
                        backgroundColor: getProductColor(index),
                        borderRadius: 1,
                      }}
                    />
                  </Box>
                </Box>
              ))}
              {Object.keys(productPatterns).length > 3 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  textAlign="center"
                >
                  +{Object.keys(productPatterns).length - 3} outros produtos
                </Typography>
              )}
            </Stack>
          ) : (
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
            >
              Sem dados
            </Typography>
          )}
        </Stack>
      </Paper>
    );
  };

  const renderWeeklyProductPattern = (weekPattern) => {
    const productPatterns = weekPattern.productPatterns || {};
    const hasData = Object.keys(productPatterns).length > 0;
    const topProducts = Object.entries(productPatterns)
      .sort(([, a], [, b]) => b.averageKitchenTime - a.averageKitchenTime)
      .slice(0, 4);

    return (
      <Paper
        key={`week-${weekPattern.weekday}`}
        elevation={2}
        sx={{
          p: 2.5,
          borderRadius: 3,
          backgroundColor: hasData
            ? alpha(theme.palette.secondary.main, 0.02)
            : alpha(theme.palette.grey[300], 0.05),
          border: `1px solid ${
            hasData
              ? alpha(theme.palette.secondary.main, 0.1)
              : alpha(theme.palette.grey[300], 0.2)
          }`,
          minHeight: 250,
        }}
      >
        <Stack spacing={2}>
          <Box textAlign="center">
            <Typography
              variant="subtitle1"
              fontWeight={700}
              color={hasData ? "secondary.main" : "text.secondary"}
            >
              {weekdayNames[weekPattern.weekday]}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {weekPattern.weekdayName}
            </Typography>
          </Box>

          {hasData ? (
            <Stack spacing={1.5}>
              {topProducts.map(([productId, data], index) => (
                <Box key={productId}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: getProductColor(index),
                        }}
                      />
                      <Typography variant="caption" fontWeight={600}>
                        {truncateProductTitle(data.productTitle, 12)}
                      </Typography>
                    </Stack>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color={getProductColor(index)}
                    >
                      {formatTime(data.averageKitchenTime)}
                    </Typography>
                  </Stack>

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    spacing={1}
                    mb={1}
                  >
                    <Box textAlign="center" flex={1}>
                      <Typography variant="caption" color="text.secondary">
                        Min
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        fontWeight={600}
                      >
                        {formatTime(data.minTime)}
                      </Typography>
                    </Box>
                    <Box textAlign="center" flex={1}>
                      <Typography variant="caption" color="text.secondary">
                        Max
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        fontWeight={600}
                      >
                        {formatTime(data.maxTime)}
                      </Typography>
                    </Box>
                    <Box textAlign="center" flex={1}>
                      <Typography variant="caption" color="text.secondary">
                        Qtd
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        fontWeight={600}
                      >
                        {data.totalQuantity}
                      </Typography>
                    </Box>
                  </Stack>

                  {data.sharedTimeInstances > 0 && (
                    <Stack direction="row" justifyContent="center">
                      <Chip
                        icon={<Group />}
                        label={`${Math.round(
                          (data.sharedTimeInstances / data.orderCount) * 100
                        )}% agrupado`}
                        size="small"
                        sx={{
                          fontSize: "0.6rem",
                          height: 16,
                          backgroundColor: alpha(
                            theme.palette.warning.main,
                            0.1
                          ),
                          color: theme.palette.warning.main,
                          "& .MuiChip-icon": { fontSize: "0.7rem" },
                        }}
                      />
                    </Stack>
                  )}
                </Box>
              ))}
              {Object.keys(productPatterns).length > 4 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  textAlign="center"
                >
                  +{Object.keys(productPatterns).length - 4} outros produtos
                </Typography>
              )}
            </Stack>
          ) : (
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
            >
              Sem dados para este dia
            </Typography>
          )}
        </Stack>
      </Paper>
    );
  };

  const renderProductSummary = () => {
    if (
      !analysisData.productSummary ||
      analysisData.productSummary.length === 0
    ) {
      return (
        <Box textAlign="center" py={4}>
          <Typography variant="body1" color="text.secondary">
            Nenhum produto encontrado no período selecionado
          </Typography>
        </Box>
      );
    }

    return (
      <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 3 }}>
        <Table>
          <TableHead>
            <TableRow
              sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}
            >
              <TableCell>
                <Typography variant="subtitle2" fontWeight={600}>
                  Produto
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="subtitle2" fontWeight={600}>
                  Tempo Médio na Cozinha
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="subtitle2" fontWeight={600}>
                  Quantidade Total
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="subtitle2" fontWeight={600}>
                  Contexto de Pedidos
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="subtitle2" fontWeight={600}>
                  Tempo Compartilhado
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {analysisData.productSummary.map((product, index) => (
              <TableRow
                key={product.productId}
                sx={{
                  "&:nth-of-type(odd)": {
                    backgroundColor: alpha(theme.palette.action.hover, 0.02),
                  },
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.02),
                  },
                }}
              >
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        backgroundColor: alpha(getProductColor(index), 0.1),
                        color: getProductColor(index),
                        fontSize: "0.75rem",
                      }}
                    >
                      <Restaurant fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {product.productTitle}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {product.productId}
                      </Typography>
                    </Box>
                  </Stack>
                </TableCell>

                <TableCell align="center">
                  <Chip
                    label={formatTime(product.averageKitchenTime)}
                    size="small"
                    sx={{
                      backgroundColor: alpha(getProductColor(index), 0.1),
                      color: getProductColor(index),
                      fontWeight: 600,
                    }}
                  />
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    {formatTime(product.minTime)} -{" "}
                    {formatTime(product.maxTime)}
                  </Typography>
                </TableCell>

                <TableCell align="center">
                  <Typography variant="body2" fontWeight={600}>
                    {product.totalQuantity}
                  </Typography>
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                  >
                    {product.orderCount} pedidos
                  </Typography>
                </TableCell>

                <TableCell align="center">
                  <Typography variant="body2" fontWeight={600}>
                    {product.averageProductsPerOrder} produtos/pedido
                  </Typography>
                  <Stack
                    direction="row"
                    justifyContent="center"
                    spacing={0.5}
                    sx={{ mt: 1 }}
                  >
                    {Object.entries(product.complexityBreakdown).map(
                      ([complexity, count]) =>
                        count > 0 && (
                          <Chip
                            key={complexity}
                            label={`${complexityLabels[complexity]}: ${count}`}
                            size="small"
                            sx={{
                              fontSize: "0.6rem",
                              height: 18,
                              backgroundColor: alpha(
                                complexityColors[complexity].bg,
                                0.2
                              ),
                              color: complexityColors[complexity].text,
                            }}
                          />
                        )
                    )}
                  </Stack>
                </TableCell>

                <TableCell align="center">
                  <Typography variant="body2" fontWeight={600}>
                    {product.sharedTimePercentage}%
                  </Typography>
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                  >
                    {product.sharedTimeInstances}/{product.orderCount} vezes
                  </Typography>
                  {product.sharedTimePercentage > 50 && (
                    <Stack
                      direction="row"
                      justifyContent="center"
                      sx={{ mt: 1 }}
                    >
                      <Chip
                        icon={<Warning />}
                        label="Frequentemente agrupado"
                        size="small"
                        sx={{
                          fontSize: "0.6rem",
                          height: 18,
                          backgroundColor: alpha(
                            theme.palette.warning.main,
                            0.1
                          ),
                          color: theme.palette.warning.main,
                          "& .MuiChip-icon": { fontSize: "0.7rem" },
                        }}
                      />
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderLoadingState = () => (
    <Card elevation={4} sx={{ borderRadius: 3 }}>
      <CardHeader
        avatar={<Restaurant />}
        title="Análise de Tempo dos Produtos na Cozinha"
        sx={{ backgroundColor: theme.palette.secondary.main }}
        titleTypographyProps={{ sx: { color: "#ffffff" } }}
        sx={{ "& .MuiCardHeader-avatar": { color: "#ffffff" } }}
      />
      <CardContent sx={{ p: 3 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={200}
        >
          <CircularProgress size={40} />
        </Box>
      </CardContent>
    </Card>
  );

  const renderErrorState = () => (
    <Card elevation={4} sx={{ borderRadius: 3 }}>
      <CardHeader
        avatar={<Restaurant />}
        title="Análise de Tempo dos Produtos na Cozinha"
        sx={{ backgroundColor: theme.palette.secondary.main }}
        titleTypographyProps={{ sx: { color: "#ffffff" } }}
        sx={{ "& .MuiCardHeader-avatar": { color: "#ffffff" } }}
      />
      <CardContent sx={{ p: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          Erro ao carregar dados de tempo dos produtos: {error}
        </Alert>
      </CardContent>
    </Card>
  );

  if (loading) return renderLoadingState();
  if (error) return renderErrorState();
  if (!analysisData) return null;

  return (
    <Card
      elevation={4}
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.secondary.main,
          0.02
        )} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
      }}
    >
      <CardHeader
        avatar={
          <Avatar sx={{ backgroundColor: "rgba(255,255,255,0.2)" }}>
            <Restaurant />
          </Avatar>
        }
        title={
          <Typography variant="h6" fontWeight={700}>
            Análise de Tempo dos Produtos na Cozinha
          </Typography>
        }
        subheader={
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
            Análise detalhada incluindo contexto de pedidos agrupados
          </Typography>
        }
        sx={{
          backgroundColor: theme.palette.secondary.main,
          color: "white",
          "& .MuiCardHeader-avatar": { mr: 2 },
        }}
      />

      <CardContent sx={{ p: 3 }}>
        {renderSummaryStats()}

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            aria-label="product time pattern tabs"
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
              },
            }}
          >
            <Tab
              icon={<Schedule />}
              iconPosition="start"
              label="Padrões por Hora"
            />
            <Tab
              icon={<CalendarToday />}
              iconPosition="start"
              label="Padrões Semanais"
            />
            <Tab
              icon={<TrendingUp />}
              iconPosition="start"
              label="Resumo dos Produtos"
            />
            <Tab
              icon={<Assessment />}
              iconPosition="start"
              label="Análise de Complexidade"
            />
          </Tabs>
        </Box>

        {tabValue === 0 && (
          <Box>
            <Typography
              variant="h6"
              fontWeight={600}
              mb={2}
              color="text.primary"
            >
              Tempo na Cozinha por Hora do Dia
            </Typography>
            <Box
              sx={{
                overflowX: "auto",
                pb: 2,
                "&::-webkit-scrollbar": {
                  height: 8,
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: alpha(theme.palette.grey[300], 0.3),
                  borderRadius: 4,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.5),
                  borderRadius: 4,
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.7),
                  },
                },
              }}
            >
              <Stack
                direction="row"
                spacing={1.5}
                sx={{ minWidth: "max-content", py: 1 }}
              >
                {analysisData.hourlyPatterns.map(renderHourlyProductPattern)}
              </Stack>
            </Box>
          </Box>
        )}

        {tabValue === 1 && (
          <Box>
            <Typography
              variant="h6"
              fontWeight={600}
              mb={2}
              color="text.primary"
            >
              Tempo na Cozinha por Dia da Semana
            </Typography>
            <Grid container spacing={2}>
              {analysisData.weeklyPatterns.map((weekPattern) => (
                <Grid item xs={12} sm={6} md={4} key={weekPattern.weekday}>
                  {renderWeeklyProductPattern(weekPattern)}
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {tabValue === 2 && (
          <Box>
            <Typography
              variant="h6"
              fontWeight={600}
              mb={2}
              color="text.primary"
            >
              Resumo Detalhado dos Produtos
            </Typography>
            {renderProductSummary()}
          </Box>
        )}

        {tabValue === 3 && renderComplexityAnalysis()}
      </CardContent>
    </Card>
  );
};

export default ProductTimePatternAnalysis;
